import React, { useEffect, useMemo, useRef, useState } from "react";

import { useCardDraft } from "../common/useCardDraft";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import Select from "components/common/Select";
import { toIsoUtc, formatShortDate } from "../chiefComplaints/dateHelpers";
import MedicineService from "./MedicineService";
import MedicineCatalogService from "./MedicineCatalogService";
import MedicineCatalogAutocomplete from "./MedicineCatalogAutocomplete";
import UnitPicker from "./UnitPicker";
import DiagnosisPicker from "./DiagnosisPicker";
import { MED_V2_REFRESH } from "./events";
import { SECTION_SELECT } from "../railEvents";
import {
  fetchSaltAllergies,
  extractSaltNamesFromCatalogRow,
  findSaltAllergyMatch,
} from "./saltAllergyCache";
import {
  DOSE_QUICKPICK,
  TIMES_OF_DAY,
  INTERVAL_OPTIONS,
  FREQ_UNIT_OPTIONS,
  INSTRUCTION_OPTIONS,
  DURATION_DAYS_QUICKPICK,
  DURATION_LONG_QUICKPICK,
} from "./data";

/**
 * Add-medicine write sheet — px-matched to Figma 2964:4270 (`Editor · A4
 * filled (Medicines)`, 340 wide).
 *
 * This was AddMedicineModal: a floating bottom-right dialog stack. The board
 * draws it as a card in the third column — the rail idiom every other v3
 * section uses — so it now lives in the editor rail driven by the shared
 * useSheetStack. All the field logic (dose quick-picks, times of day,
 * intervals, custom frequency, duration + Ongoing, auto end-date, diagnosis
 * link) carries over from the modal; only the shell and the controls'
 * rendering changed to the board's segmented strips.
 *
 * The board's word budget: description follows the sections-wide 250-word
 * rule with the live tally the other sheets carry.
 */

const MAX_DESCRIPTION_WORDS = 250;
const countWords = (s) => {
  const t = (s || "").trim();
  return t ? t.split(/\s+/).length : 0;
};
const clampWords = (next, max) => {
  if (countWords(next) <= max) return next;
  const parts = next.split(/(\s+)/);
  let words = 0;
  const out = [];
  for (const chunk of parts) {
    if (/^\s+$/.test(chunk)) { out.push(chunk); continue; }
    if (chunk === "") continue;
    if (words >= max) break;
    out.push(chunk);
    words += 1;
  }
  return out.join("").replace(/\s+$/, "");
};


const addDays = (isoDate, days) => {
  const d = new Date(isoDate);
  if (isNaN(d.getTime()) || !days) return null;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// "08 Jan 2026" — the helper row's display format on the board.
const shortDate = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

/* Board 2964:4280 `icon/chev-up-16` — the card-collapse affordance, same as
   every other rail sheet. */
const CollapseChevron = () => (
  <svg viewBox="0 0 11 11" width="16" height="16" fill="none" aria-hidden="true">
    <path
      d="M2 7.5L5.5 3.5L9 7.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Board 3216:2693 — the Material calendar at 15px, same glyph the status
   sheets pin on their date fields. */
const CalendarIcon = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
    <path
      d="M13.3333 2H12.6667V0.666667H11.3333V2H4.66667V0.666667H3.33333V2H2.66667C1.93333 2 1.33333 2.6 1.33333 3.33333V14C1.33333 14.7333 1.93333 15.3333 2.66667 15.3333H13.3333C14.0667 15.3333 14.6667 14.7333 14.6667 14V3.33333C14.6667 2.6 14.0667 2 13.3333 2ZM13.3333 14H2.66667V6.66667H13.3333V14ZM13.3333 5.33333H2.66667V3.33333H13.3333V5.33333Z"
      fill="#26284D"
    />
  </svg>
);

/* Formulation catalog lookup — one call to api-medicine-service's
   `/api/v1/medicine/formulations` returns the full enumerated set
   (~125 rows), each row shaped as { code, label, units[], instructions[] }.
   Cached module-level and shared across every mounted form so the
   fetch only happens once per page load. The consumer sites read
   the whole catalog map by code:
     FORMULATION_CATALOG[REFLD177] → { label, units, instructions }
   Empty maps on fetch failure so the section still renders (labels /
   units / instructions all become "unknown", picker still works). */
let FORMULATION_CATALOG = null;    // { [code]: { label, units, instructions } }
let FORMULATION_INFLIGHT = null;

export const getFormulationCatalog = () => FORMULATION_CATALOG;

export const fetchFormulationCatalog = () => {
  if (FORMULATION_CATALOG) return Promise.resolve(FORMULATION_CATALOG);
  if (FORMULATION_INFLIGHT) return FORMULATION_INFLIGHT;
  FORMULATION_INFLIGHT = new Promise((resolve) => {
    new MedicineCatalogService().formulations(
      (rows) => {
        const catalog = {};
        (rows || []).forEach((r) => {
          if (!r || !r.code) return;
          catalog[r.code] = {
            label:        r.label || "",
            units:        Array.isArray(r.units) ? r.units : [],
            instructions: Array.isArray(r.instructions) ? r.instructions : [],
          };
        });
        FORMULATION_CATALOG = catalog;
        FORMULATION_INFLIGHT = null;
        resolve(FORMULATION_CATALOG);
      },
      () => {
        // Fetch failure is non-fatal — pickers fall back to raw REFLD /
        // free-text unit / hidden instructions.
        FORMULATION_INFLIGHT = null;
        resolve({});
      },
    );
  });
  return FORMULATION_INFLIGHT;
};

/* Special slot key used inside `timesOfDay[]` for the "As needed" pill
   in Times-of-day mode. Kept as a string constant so the payload shape
   matches Med v2's DoseFrequency.timesOfDay ([]string) — no schema
   change on the wire. */
export const AS_NEEDED = "asNeeded";

const DOSE_QUICKPICK_SET = new Set(DOSE_QUICKPICK);

/* Dose-quantity value bounds: > 0 and <= 999. Value is stored as a
   text string so the fractional idioms in DOSE_QUICKPICK ("½", "1½")
   don't lose precision to float conversion — the clamp only touches
   numeric input. Empty is allowed (reviewer clearing to retype).

   `clampDoseValue(v)` — pipes onChange from every dose Qty input:
     empty                                   → returned unchanged
     DOSE_QUICKPICK preset (e.g. "1/2")      → preserved verbatim
     otherwise                               → non-digits stripped, then
                                               integer clamped to [1, 999]
     (A lone "0" is rejected as ""; typing "1" then "0" keeps working
     because onChange fires with "10" — the clamp sees the full string.) */
export const clampDoseValue = (v) => {
  if (v === "" || v == null) return v;
  const s = String(v);
  // Preset chips (e.g. "1/2") are set programmatically and bypass the
  // integer rule so the SegStrip presets keep working.
  if (DOSE_QUICKPICK_SET.has(s)) return s;
  // Integer-only typed input: strip non-digits, clamp to [1, 999].
  const digits = s.replace(/\D/g, "");
  if (digits === "") return "";
  const n = parseInt(digits, 10);
  if (n < 1) return "";
  if (n > 999) return "999";
  return String(n);
};
export const isPositiveDose = (v) => {
  const s = String(v || "").trim();
  if (!s) return false;
  if (DOSE_QUICKPICK_SET.has(s)) return true;
  const n = parseInt(s, 10);
  if (isNaN(n)) return false;
  return n >= 1 && n <= 999;
};

/* Board 2964:4305 etc. — one bordered r10 strip, equal flex cells split by
   hairlines; an ON cell floods #6a5ef5. `rows` is an array of arrays so the
   frequency strip can stack times-of-day over intervals in one border. */
export const SegStrip = ({ rows, isOn, onToggle, disabled, boldOn, footer }) => (
  <div className="med3-seg" role="group">
    {rows.map((row, ri) => (
      <div className="med3-seg-row" key={ri}>
        {row.map((cell) => (
          <button
            key={cell.key}
            type="button"
            className={`med3-seg-cell${isOn(cell.key) ? " is-on" : ""}${
              boldOn ? " is-boldon" : ""
            }`}
            aria-pressed={isOn(cell.key)}
            onClick={() => onToggle(cell.key)}
            disabled={disabled}
          >
            {cell.label}
          </button>
        ))}
      </div>
    ))}
    {footer}
  </div>
);
SegStrip.propTypes = {
  rows: PropTypes.array.isRequired,
  isOn: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  /** ON cells render Bold (the dose/frequency strips); duration's stay 500. */
  boldOn: PropTypes.bool,
  /** Extra content painted INSIDE the bordered card, below the chip rows —
   *  Duration's Custom-days input row lives here. */
  footer: PropTypes.node,
};

const MedicineForm = ({
  patientId,
  formId,
  ordinal,
  showOrdinal,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClose,
  // Transcript-mode extensions (all optional; defaults preserve standalone).
  initialPayload,
  onSave,
  onReject,
  saving,
  error,
  themeClass,
  lockName,
  blockedMcodes,
  menuClassName,
  isReopen,
}) => {
  const p = initialPayload || null;
  const isTranscript = typeof onSave === "function";
  // Name + formulation are chosen at first-time add. Re-open in transcript
  // mode (e.g. review click-to-edit) locks them so the reviewer can only
  // tweak dose / frequency / duration / description from there. Mirrors
  // ComplaintForm's `lockComplaint` / DiagnosisForm's `lockName`.
  const lockMedicineName = !!lockName;

  // ── medicine identity ────────────────────────────────────────────
  // Transcript-mode add pre-fills from `initialPayload` (agent's
  // `add_medication` output). Agent doesn't have a Med v2 mCode
  // (different code system), so `medicineCode` stays null on AI-add
  // path — the reviewer picks a catalog match on standalone edit
  // after Save & next.
  // ZY-10356 Phase 1 — transcript-mode draft preservation. Standalone
  // mounts return [null, no-op]; the rest of this block is inert
  // outside the modal. Each useState below hydrates from `draft` when
  // present; the useEffect at the end syncs persistent fields back.
  const [draft, saveDraft] = useCardDraft(formId);

  const [medicine, setMedicine] = useState(() => {
    if (draft && "medicine" in draft) return draft.medicine;
    if (p) return p.medicine || p.name || p.value || "";
    return "";
  });
  // Reedit cards open with `lockName` so the catalog autocomplete is
  // hidden — there's no UI path to set medicineCode after mount. Seed
  // it from the captured payload's mcode / formulation_name /
  // manufacturer_name (written by medicineBodyAdapters.bodyToCapturedAdd
  // the first time the reviewer saved) so `hasCatalogBinding` is true
  // on reopen and the Add button can enable once the rest of the form
  // is valid. AI proposals never carry `mcode`; the autocomplete's
  // mount-time auto-match populates medicineCode on that path instead.
  const [medicineCode, setMedicineCode] = useState(() => {
    if (draft && "medicineCode" in draft) return draft.medicineCode;
    if (p && p.mcode) {
      return {
        mCode: p.mcode,
        formulationName: p.formulation_name || "",
        manufacturerName: p.manufacturer_name || "",
      };
    }
    return null;
  });
  const [formulation, setFormulation] = useState(() => (
    draft && "formulation" in draft ? draft.formulation : (p ? p.formulation || "" : "")
  ));
  const [formulationName, setFormulationName] = useState(() => (
    draft && "formulationName" in draft
      ? draft.formulationName
      : (p ? p.formulation_name || "" : "")
  ));
  // Formulation catalog — REFLD → { label, units, instructions }. Seeded
  // from the module-level cache so re-mounts don't re-fetch. Drives the
  // formulation pill, the Dose-quantity unit input (0/1/N-unit UX), AND
  // the Instructions chip set — one fetch, one source of truth.
  const [formulationCatalog, setFormulationCatalog] = useState(
    () => getFormulationCatalog() || {},
  );
  useEffect(() => {
    let alive = true;
    fetchFormulationCatalog().then((r) => {
      if (!alive || !r) return;
      if (Object.keys(r).length) setFormulationCatalog(r);
    });
    return () => { alive = false; };
  }, []);
  const [diagnosisId, setDiagnosisId] = useState(() => (
    draft && "diagnosisId" in draft ? draft.diagnosisId : null
  ));

  // ── dose ─────────────────────────────────────────────────────────
  // Two shapes coexist per the redesigned mock:
  //   Times-of-day mode → `perSlotQuantities` (map of slot → {value,unit}).
  //     One row PER selected slot, so the same drug can carry Morning=2 /
  //     Night=1. Cleared when the reviewer switches to Custom mode or
  //     picks the special "asNeeded" slot below.
  //   Custom mode / Ointment-like formulations → the scalar `doseValue` /
  //     `doseUnit` pair, unchanged from before.
  // Transcript-mode seeding parses the AI's verbatim `dose_qty` ("1 tab",
  // "500 mg") into the scalar pair — the AI has no notion of per-slot
  // dosing so a Custom-mode default is the safe landing.
  const [doseValue, setDoseValue] = useState(() => {
    if (draft && "doseValue" in draft) return draft.doseValue;
    if (!p || !p.dose_qty) return "";
    const s = String(p.dose_qty).trim();
    const sp = s.indexOf(" ");
    return sp > 0 ? s.slice(0, sp).trim() : s;
  });
  const [doseUnit, setDoseUnit] = useState(() => {
    if (draft && "doseUnit" in draft) return draft.doseUnit;
    if (!p || !p.dose_qty) return "";
    const s = String(p.dose_qty).trim();
    const sp = s.indexOf(" ");
    return sp > 0 ? s.slice(sp + 1).trim() : "";
  });
  // Per-slot quantity map — { Morning:{value,unit}, Afternoon:{...}, ... }.
  // Populated on each Times-of-day chip toggle; empty in Custom mode.
  const [perSlotQuantities, setPerSlotQuantities] = useState(() => (
    draft && "perSlotQuantities" in draft ? draft.perSlotQuantities : {}
  ));

  // ── frequency ────────────────────────────────────────────────────
  // Best-effort parse of AI's free-text frequency into the times-of-day
  // set. Idiom shortcuts + times-of-day keyword scan first; "6h" / "12
  // hours" idioms → Custom-mode custom (value+unit) pair; otherwise
  // leave all empty so the reviewer picks. Returns the mode the seed
  // lands in too so the mode toggle initialises correctly.
  const seedFrequency = () => {
    if (!p || !p.frequency) return { mode: "timesOfDay", tod: [], cfv: "", cfu: "Hours" };
    const f = String(p.frequency).toLowerCase();
    const tod = [];
    if (f.includes("morning")) tod.push("Morning");
    if (f.includes("afternoon") || f.includes("noon")) tod.push("Afternoon");
    if (f.includes("evening") || f.includes("supper")) tod.push("Evening");
    if (f.includes("night") || f.includes("bedtime")) tod.push("Night");
    if (tod.length > 0) return { mode: "timesOfDay", tod, cfv: "", cfu: "Hours" };
    const idiom = {
      "once daily": ["Morning"], "once a day": ["Morning"], "od": ["Morning"], "qd": ["Morning"],
      "twice daily": ["Morning", "Night"], "twice a day": ["Morning", "Night"], "bid": ["Morning", "Night"],
      "three times daily": ["Morning", "Afternoon", "Night"], "three times a day": ["Morning", "Afternoon", "Night"], "tid": ["Morning", "Afternoon", "Night"],
      "four times daily": ["Morning", "Afternoon", "Evening", "Night"], "four times a day": ["Morning", "Afternoon", "Evening", "Night"], "qid": ["Morning", "Afternoon", "Evening", "Night"],
    }[f];
    if (idiom) return { mode: "timesOfDay", tod: idiom, cfv: "", cfu: "Hours" };
    // "6h" / "8 hours" idioms → Custom mode with the number in
    // customFreqValue + "Hours" as the unit. Interval chips are now
    // pure shortcuts that populate the same (value, unit) pair, so
    // there's no separate `interval` state to seed.
    for (const v of ["4", "6", "8", "12"]) {
      if (f === v + "h" || f === v + " hours" || f === v + " hour") {
        return { mode: "custom", tod: [], cfv: v, cfu: "Hours" };
      }
    }
    if (f.includes("as needed") || f === "prn") {
      return { mode: "timesOfDay", tod: [AS_NEEDED], cfv: "", cfu: "Hours" };
    }
    return { mode: "timesOfDay", tod: [], cfv: "", cfu: "Hours" };
  };
  const _seededFreq = seedFrequency();
  // Explicit mode toggle for the whole frequency block. "timesOfDay" =
  // named slots + optional As-needed; "custom" = number + unit pair
  // (interval chips are shortcuts). Kept as its own state (not derived)
  // so the reviewer can flip modes without losing UI focus.
  const [frequencyMode, setFrequencyMode] = useState(() => (
    draft && "frequencyMode" in draft ? draft.frequencyMode : _seededFreq.mode
  ));
  const [timesOfDay, setTimesOfDay] = useState(() => (
    draft && "timesOfDay" in draft ? draft.timesOfDay : _seededFreq.tod
  ));
  const [customFreqValue, setCustomFreqValue] = useState(() => (
    draft && "customFreqValue" in draft ? draft.customFreqValue : _seededFreq.cfv
  ));
  // Default to "Hours" so clicking an interval chip (which sets Hours
  // implicitly) doesn't need to override an unrelated default.
  const [customFreqUnit, setCustomFreqUnit] = useState(() => (
    draft && "customFreqUnit" in draft ? draft.customFreqUnit : _seededFreq.cfu
  ));

  // ── instructions ─────────────────────────────────────────────────
  // AI's `instructions` is verbatim ("Before meal", "After meal", "With
  // water"). Normalise common phrases to Med v2's enum keys so the
  // segmented picker highlights correctly. Anything unrecognised stays
  // blank — reviewer picks explicitly.
  const [instructions, setInstructions] = useState(() => {
    if (draft && "instructions" in draft) return draft.instructions;
    if (!p || !p.instructions) return "";
    const raw = String(p.instructions).toLowerCase().trim();
    if (raw.includes("before")) return "before";
    if (raw.includes("after dinner")) return "after-dinner";
    if (raw.includes("after")) return "after";
    if (raw.includes("with")) return "with";
    if (raw.includes("empty")) return "empty";
    return "";
  });

  // ── dates + duration ─────────────────────────────────────────────
  // Transcript-mode: seed medStartDate from AI's start_date; default to
  // today when unstated (Med v2 requires a real date on write).
  const [medStartDate, setMedStartDate] = useState(() => {
    if (draft && "medStartDate" in draft) return draft.medStartDate;
    if (p && (p.start_date || p.since_date)) return p.start_date || p.since_date;
    return "";
  });
  // Ongoing defaults on if AI's status="ongoing" AND no numeric duration.
  const [ongoing, setOngoing] = useState(() => {
    if (draft && "ongoing" in draft) return draft.ongoing;
    if (!p) return false;
    const st = String(p.status || "").toLowerCase();
    const per = String(p.time_period || "").toLowerCase();
    if (per.includes("life") || per.includes("further") || per.includes("indefinite")) return true;
    return st === "ongoing" && !p.time_period;
  });
  // Mount-only hydration for duration + its "which chip was last clicked"
  // marker. ZY-10387 — the quick-pick isOn gate requires BOTH
  // durationDays === chipDays AND lastPickRef.current === chipKey, so an
  // AI-seeded durationDays (e.g. "10 days") sets the state but never
  // lights a chip. The reviewer sees an empty duration row and assumes
  // the AI dropped the value. Deriving `pickKey` + `custom` from the
  // seed here lets the matching chip light on hydration, OR lands the
  // raw number in the Custom input when it doesn't match a quick-pick.
  const seededDuration = useMemo(() => {
    const seat = (days) => {
      if (!days) return { days: 0, pickKey: "", custom: "" };
      if (DURATION_DAYS_QUICKPICK.includes(days)) {
        return { days, pickKey: String(days), custom: "" };
      }
      const long = DURATION_LONG_QUICKPICK.find((o) => o.days === days);
      if (long) return { days, pickKey: long.label, custom: "" };
      return { days, pickKey: "", custom: String(days) };
    };
    if (draft && "durationDays" in draft) {
      // Draft already carries customDays verbatim — a non-empty value
      // means the reviewer was on the Custom row; respect that over
      // the quick-pick auto-match so a draft of (durationDays=10,
      // customDays="10") doesn't jump the pick to the 10-day chip.
      const draftCustom = (draft && draft.customDays) || "";
      if (draftCustom) return { days: draft.durationDays, pickKey: "", custom: draftCustom };
      return seat(draft.durationDays || 0);
    }
    if (!p || !p.time_period) return { days: 0, pickKey: "", custom: "" };
    const per = String(p.time_period).toLowerCase().trim();
    const digits = per.match(/^\d+/);
    if (!digits) return { days: 0, pickKey: "", custom: "" };
    const n = parseInt(digits[0], 10);
    if (!Number.isFinite(n) || n <= 0) return { days: 0, pickKey: "", custom: "" };
    let days = n;
    if (per.includes("month")) days = n * 30;
    else if (per.includes("week")) days = n * 7;
    else if (per.includes("year")) days = n * 365;
    return seat(days);
  // Mount-only — later edits route through setDurationDays /
  // setCustomDays / lastPickRef.current directly, not this derivation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [durationDays, setDurationDays] = useState(() => seededDuration.days);
  const [customDays, setCustomDays] = useState(() => seededDuration.custom);

  // ── description + sheet state ────────────────────────────────────
  const [description, setDescription] = useState(() => (
    draft && "description" in draft ? draft.description : (p ? p.description || "" : "")
  ));
  const [collapsed, setCollapsed] = useState(false);

  // ZY-10356 Phase 1 — sync persistent fields to the modal-root draft
  // store on every change. No-op in standalone (saveDraft is no-op
  // outside the CardDraftProvider).
  useEffect(() => {
    saveDraft({
      medicine, medicineCode, formulation, formulationName, diagnosisId,
      doseValue, doseUnit, perSlotQuantities,
      frequencyMode, timesOfDay, customFreqValue, customFreqUnit,
      instructions, medStartDate, ongoing, durationDays, customDays,
      description,
    });
  }, [
    saveDraft,
    medicine, medicineCode, formulation, formulationName, diagnosisId,
    doseValue, doseUnit, perSlotQuantities,
    frequencyMode, timesOfDay, customFreqValue, customFreqUnit,
    instructions, medStartDate, ongoing, durationDays, customDays,
    description,
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  // Which duration cell was actually clicked. 7 days and "1 week" are the
  // same day-count reachable from both rows; without this both would light.
  // Seeded from `seededDuration.pickKey` so an AI-hydrated durationDays
  // lights the right chip without the reviewer having to re-click (and so
  // the standalone opens blank just like before — seededDuration.pickKey
  // comes out "" when p + draft are both absent).
  const lastPickRef = useRef(seededDuration.pickKey);
  // Handle on the catalog autocomplete so we can restore focus to the
  // search input after the "cannot prescribe" dialog closes.
  const catalogRef = useRef(null);

  // The rail's blank-sheet guard: this sheet counts as touched once a
  // medicine name is on it. Only invoked when the caller wired the
  // callback (standalone SheetStack does; transcript doesn't).
  useEffect(() => {
    if (typeof onFilledChange === "function") {
      onFilledChange(formId, !!medicine.trim());
    }
  }, [formId, medicine, onFilledChange]);

  // ── allergy guard ────────────────────────────────────────────────
  // Salt-category allergies — the record's `.name` field is the salt
  // (e.g. "Amoxicillin"). The guard compares that against the salts of
  // whatever medicine the clinician is trying to pick, so an Amoxicillin
  // allergy blocks every brand containing Amoxicillin (Moxikind-CV,
  // Amoxil, etc.) without any medicine-name magic.
  const [saltAllergies, setSaltAllergies] = useState([]);
  // Match result for a rejected DROPDOWN pick — carries the matched
  // allergy AND the shared salt name so the dialog can explain why.
  const [blockingMatch, setBlockingMatch] = useState(null);
  const [allergyDialogOpen, setAllergyDialogOpen] = useState(false);
  // Set of mCodes already active on this patient. Powers the "already
  // added" highlight in the catalog dropdown so the reviewer can't pick
  // a duplicate (backend used to catch this at Save time with an
  // error message; this moves the gate up-front). Empty in transcript
  // mode — transcript callers pass `blockedMcodes` (currently active
  // Med v2 rows + in-call rejects + in-call adds) so dedup covers the
  // same ground without double-fetching Med v2. Past (completed /
  // discontinued) entries are intentionally excluded from both sets:
  // a patient may be re-prescribed a drug they finished earlier.
  const [activeMcodes, setActiveMcodes] = useState(() => new Set());

  useEffect(() => {
    if (!patientId) return;
    let alive = true;
    fetchSaltAllergies(patientId).then((rows) => {
      if (alive) setSaltAllergies(rows);
    });
    return () => { alive = false; };
  }, [patientId]);

  useEffect(() => {
    if (!patientId || isTranscript) return;
    let alive = true;
    const load = () => {
      new MedicineService().list(
        patientId,
        (res) => {
          if (!alive) return;
          const s = new Set();
          ((res && res.active) || []).forEach((m) => {
            const c = m && m.medicineCode && m.medicineCode.mCode;
            if (c) s.add(c);
          });
          setActiveMcodes(s);
        },
        () => { /* non-fatal — dedup falls back to server-side error on Save */ },
      );
    };
    load();
    EventService.addListener(MED_V2_REFRESH, load);
    return () => {
      alive = false;
      EventService.removeListener(MED_V2_REFRESH, load);
    };
  }, [patientId, isTranscript]);

  const isDropdownItemAlreadyActive = (item) => {
    const code = item && item.mCode;
    if (!code) return false;
    if (activeMcodes.has(code)) return true;
    if (blockedMcodes && blockedMcodes.has(code)) return true;
    return false;
  };

  // Both CTAs clear the block state too — the user never actually landed
  // the allergen on the medicine field (we intercepted the pick), so there
  // is no persistent block to preserve after the dialog closes.
  const dismissAllergyDialog = () => {
    setAllergyDialogOpen(false);
    setBlockingMatch(null);
    // Return focus to the search input so the clinician can immediately
    // pick a different medicine without reaching for the mouse.
    if (catalogRef.current) catalogRef.current.focus();
  };
  const openAllergiesSection = () => {
    EventService.emit(SECTION_SELECT, "allergies");
    setAllergyDialogOpen(false);
    setBlockingMatch(null);
  };

  // Autocomplete asks whether a raw catalog row should be blocked —
  // returns {allergy, matchType: "salt", matchDetail: sharedSaltName}
  // or null. Set-intersection of picked medicine's saltNames with the
  // patient's recorded salt allergies.
  const isDropdownItemBlocked = (item) =>
    findSaltAllergyMatch(
      extractSaltNamesFromCatalogRow(item),
      saltAllergies
    );

  // Fired when the user clicks a grayed-out row in the dropdown. Show
  // the "cannot prescribe" dialog — the medicine field is NOT touched
  // (no state change on the form) so canceling the dialog leaves the
  // sheet exactly as the user left it.
  const onBlockedCatalogPick = (match) => {
    setBlockingMatch(match);
    setAllergyDialogOpen(true);
  };

  const setMedicineFromCatalog = ({
    medicine: m,
    medicineCode: mc,
    formulation: f,
    formulationName: fn,
  }) => {
    // A safe pick landed on the field — clear any previous block state.
    setBlockingMatch(null);
    setAllergyDialogOpen(false);
    setMedicine(m);
    setMedicineCode(mc);
    setFormulation(f || (mc && mc.formulation) || "");
    setFormulationName(fn || (mc && mc.formulationName) || "");
  };

  // Dose-frequency inputs are three MUTUALLY EXCLUSIVE groups:
  //   1) Times of day       (Morning · Afternoon · Evening · Night — set)
  //   2) Interval buttons   (4h · 6h · 8h · 12h — scalar)
  //   3) Custom frequency   (customValue + customUnit — pair)
  // Selecting from any one group clears the other two so the wire only
  // ever carries a single frequency intent.
  // Mode toggle handler — flips the whole frequency block WITHOUT
  // wiping the other mode's state. The reviewer can toggle Times-of-
  // day ↔ Custom to preview the alternate shape without losing their
  // original picks (particularly important on the Update surface where
  // the record already carries values). The save body filters by
  // `frequencyMode` so the inactive mode's stale values never reach
  // the wire.
  const switchFrequencyMode = (next) => {
    if (next === frequencyMode) return;
    // Custom mode shows the scalar Qty + Unit pair — seed the unit if
    // the scalar is blank so the picker doesn't drop to "None".
    if (next === "custom") seedScalarUnitIfEmpty();
    setFrequencyMode(next);
  };

  // Seed unit for a NEW per-slot row. Uses the picked scalar `doseUnit`
  // if it's set (reviewer already chose a unit — reuse it across slots),
  // else the FIRST Recommended unit (ZY-10312 — multi-unit formulations
  // previously fell through to "" and seeded the slot as None, greying
  // out the Qty box with no signal that a unit was expected). Matches
  // the scalar useEffect below which also defaults to availableUnits[0]
  // on a fresh formulation. Empty when the catalog offers no units.
  const seedSlotUnit = () => doseUnit || availableUnits[0] || "";

  // Seed the SCALAR doseUnit when the UI is about to switch to a view
  // that reads it (As-needed row, Custom frequency row). Defensive —
  // the useEffect below already seeds the scalar on a fresh formulation,
  // but a reviewer who manually clears the unit and then switches modes
  // would otherwise land in "None" with the Qty disabled.
  const seedScalarUnitIfEmpty = () => {
    if (doseUnit) return;
    const anySlotUnit = Object.values(perSlotQuantities)
      .map((q) => q && q.unit)
      .find(Boolean);
    const seed = anySlotUnit || availableUnits[0] || "";
    if (seed) setDoseUnit(seed);
  };

  // Per-slot quantity setters — used by the row inputs rendered under
  // "Dose quantity" when Times-of-day mode has named slots selected.
  //
  // UX-only fan-out: when the reviewer fills the first slot AND every
  // OTHER selected named slot is still empty, copy the value across
  // (same for the unit when the reviewer hand-picks one from a
  // multi-Recommended list). Once ANY other slot carries a value /
  // unit the copy stops firing, so a differentiated regimen the
  // reviewer set intentionally is never overwritten.
  const setSlotValue = (slot, value) => {
    setPerSlotQuantities((prev) => {
      // Guard: a None-unit slot never carries a qty. If the picker
      // somehow calls onChange on a disabled input (autofill, paste,
      // race with the disabled attr taking effect), swallow the value.
      const currentUnit = (prev[slot] && prev[slot].unit) || "";
      if (!currentUnit) return prev;
      // Rule 1: qty change NEVER touches units — this slot's unit is
      // preserved as-is, and the fan-out below leaves every other slot's
      // unit alone. A qty change is a qty change.
      const nextRow = { value, unit: currentUnit };
      // Rule 3: cascade the qty into the slots BELOW only when the
      // source is the FIRST slot AND every (non-None) below slot still
      // tracks the source's PREVIOUS value. That gives keystroke-by-
      // keystroke mirroring ("4" → "45" → "456") while the reviewer is
      // typing at the top; the moment they touch any below slot the
      // mirror stops. Typing in any slot other than the first is always
      // row-local — no cascade. None-unit below slots are ignored.
      const isFirstSlot = namedSlots.length > 1 && slot === namedSlots[0];
      const sourceOld = (prev[slot] && prev[slot].value) || "";
      const fanOut =
        isFirstSlot &&
        namedSlots.every((s) => {
          if (s === slot) return true;
          const su = (prev[s] && prev[s].unit) || "";
          if (!su) return true;
          return ((prev[s] && prev[s].value) || "") === sourceOld;
        });
      if (!fanOut) return { ...prev, [slot]: nextRow };
      const next = { ...prev, [slot]: nextRow };
      namedSlots.forEach((s) => {
        if (s === slot) return;
        const siblingUnit = (prev[s] && prev[s].unit) || "";
        // A sibling whose unit is None never receives the cascaded qty.
        // Preserve its own value (which should already be blank because
        // the qty box is disabled when unit is None).
        if (!siblingUnit) {
          next[s] = { value: (prev[s] && prev[s].value) || "", unit: "" };
          return;
        }
        // Preserve sibling's own unit (rule 1 forbids us from touching
        // it here).
        next[s] = { value, unit: siblingUnit };
      });
      return next;
    });
  };
  const setSlotUnit = (slot, unit) => {
    setPerSlotQuantities((prev) => {
      // Picking None (empty unit) clears the paired Qty so a stale value
      // doesn't linger in a greyed-out box on save/reload.
      const value = unit ? ((prev[slot] && prev[slot].value) || "") : "";
      const nextRow = { value, unit };
      // Rule 4: cascade the unit only from the FIRST slot and only
      // while every below slot is still on None. Once any below slot
      // has its own unit picked, unit changes on the first slot stay
      // row-local. Picking None on the first slot also stays local
      // (None doesn't cascade).
      const isFirstSlot = namedSlots.length > 1 && slot === namedSlots[0];
      const fanOut =
        isFirstSlot &&
        String(unit).trim() !== "" &&
        namedSlots.every((s) => s === slot
          ? true
          : !String((prev[s] && prev[s].unit) || "").trim());
      if (!fanOut) return { ...prev, [slot]: nextRow };
      const next = { ...prev, [slot]: nextRow };
      namedSlots.forEach((s) => {
        if (s === slot) return;
        next[s] = { value: (prev[s] && prev[s].value) || "", unit };
      });
      return next;
    });
  };

  const toggleTimeOfDay = (t) => {
    if (t === AS_NEEDED) {
      // As-needed is mutually exclusive with every named slot: toggling it
      // on wipes the named-slot picks and their per-slot quantities;
      // toggling it off just removes it.
      const enteringAsNeeded = !timesOfDay.includes(AS_NEEDED);
      setTimesOfDay((prev) => {
        const has = prev.includes(AS_NEEDED);
        if (has) return prev.filter((x) => x !== AS_NEEDED);
        return [AS_NEEDED];
      });
      setPerSlotQuantities({});
      // As-needed renders the scalar Qty + Unit pair, so seed the
      // scalar unit (ZY-10312) when the record came in per-slot with
      // a blank scalar.
      if (enteringAsNeeded) seedScalarUnitIfEmpty();
      return;
    }
    // Named slot — if As-needed was on, drop it so the two never coexist.
    const seedUnit = seedSlotUnit();
    setTimesOfDay((prev) => {
      const withoutAsNeeded = prev.filter((x) => x !== AS_NEEDED);
      if (withoutAsNeeded.includes(t)) {
        // Deselecting → also drop that slot's per-slot quantity.
        setPerSlotQuantities((qs) => {
          const { [t]: _, ...rest } = qs;
          return rest;
        });
        return withoutAsNeeded.filter((x) => x !== t);
      }
      // Selecting → seed a blank per-slot entry so the row's inputs render.
      setPerSlotQuantities((qs) => ({
        ...qs,
        [t]: qs[t] || { value: "", unit: seedUnit },
      }));
      return [...withoutAsNeeded, t];
    });
  };
  // Interval chips are pure shortcuts that populate the custom
  // (value + unit) pair below. `iv` is like "8h" → number = "8",
  // unit = "Hours". Clicking a chip whose pair is already selected
  // toggles the pair off (clears value + resets unit to "Hours").
  const pickInterval = (iv) => {
    const n = iv.replace(/h$/, "");
    if (customFreqValue === n && customFreqUnit === "Hours") {
      setCustomFreqValue("");
      return;
    }
    setCustomFreqValue(n);
    setCustomFreqUnit("Hours");
  };
  // `isOn(chip)` helper for the interval chip row — highlighted only
  // when the value+unit pair below exactly matches the chip.
  const intervalChipMatches = (iv) => {
    const n = iv.replace(/h$/, "");
    return customFreqValue === n && customFreqUnit === "Hours";
  };
  // Per-unit bounds for the custom-frequency value:
  //   Hours              → [1, 23]  (24+ belongs in the Days unit)
  //   Days/Weeks/Months  → [1, 999] (min 1 — 0 has no meaning as a
  //                                  frequency; max 999 keeps a
  //                                  sane upper guard)
  // Empty string is always allowed so the reviewer can clear + retype.
  const customFreqMax = (u) => (u === "Hours" ? 23 : 999);
  const clampCustomFreq = (v, u) => {
    if (v === "") return v;
    const n = parseInt(v, 10);
    if (isNaN(n)) return v;
    const max = customFreqMax(u);
    if (n > max) return String(max);
    if (n < 1)   return "1";
    return v;
  };
  const changeCustomFreqValue = (v) => {
    setCustomFreqValue(clampCustomFreq(v, customFreqUnit));
  };
  const changeCustomFreqUnit = (u) => {
    if (customFreqValue) {
      const clamped = clampCustomFreq(customFreqValue, u);
      if (clamped !== customFreqValue) setCustomFreqValue(clamped);
    }
    setCustomFreqUnit(u);
  };

  const pickDays = (n) => {
    setDurationDays(n);
    setCustomDays("");
  };

  const effectiveDurationDays = ongoing
    ? 0
    : customDays
    ? Math.max(0, parseInt(customDays, 10) || 0)
    : durationDays;

  const derivedEndDate =
    !ongoing && effectiveDurationDays > 0
      /* `durationDays - 1`: a 10-day course that STARTS on Oct 1 covers
         Oct 1 (day 1) through Oct 10 (day 10) — NOT Oct 11. Previously
         we added the raw duration, which pushed the end date one day
         past the actual last dose and left the past-medicine table
         showing contradictions like "01 Oct – 11 Oct · 10 days". */
      ? addDays(medStartDate, effectiveDurationDays - 1)
      : null;

  // Custom-frequency pair rule: if EITHER the number or the unit is set,
  // BOTH must be set — "2" with no unit means nothing on the wire, and a
  // unit with no number is a stray pick. (When both are empty, that's a
  // legitimate "just an interval" pick.)
  // Custom-freq intent is expressed only when the number field is
  // filled — unit alone means nothing on the wire. When there's a
  // value, unit must accompany it. With the default unit now "Days"
  // this branch reduces to "any value is fine" but keeps the guard
  // for the edge case where the reviewer manually blanks the unit.
  const customFreqPairOK = !customFreqValue || !!customFreqUnit;

  // Frequency validity depends on the mode toggle:
  //   Times-of-day → at least one slot picked (named slot OR As-needed).
  //   Custom       → a valid (value+unit) pair; interval chips are pure
  //                  shortcuts that populate the same pair.
  const hasFrequency =
    frequencyMode === "timesOfDay"
      ? timesOfDay.length > 0
      : !!customFreqValue && !!customFreqUnit;

  // Dose validity — three shapes:
  //   Ointment-like → the free-text field must be non-empty.
  //   Times-of-day + As-needed → dose block is hidden; nothing to require.
  //   Times-of-day + named slots → EVERY selected slot must have a value.
  //   Custom mode → either the scalar dose is set OR the As-needed
  //                 checkbox is on (dose then means "as needed", no qty).
  // Available units for the picked formulation — driven by the DB's
  // Formulation catalog entry — carries the units[] and instructions[]
  // for this REFLD code. Undefined when the code isn't in the catalog
  // (legacy row / catalog fetch failed) — falls back to free-text unit
  // input and a hidden Instructions section, same as pre-import.
  const catalogEntry = formulationCatalog[formulation];
  const availableUnits = (catalogEntry && catalogEntry.units) || [];
  const catalogInstructions = (catalogEntry && catalogEntry.instructions) || [];
  // Global unit vocabulary — union of every formulation's units[]
  // across the catalog. Powers the UnitPicker's "Other" section
  // (rest of the units, alphabetised, minus the current
  // formulation's Recommended set). Recomputed only when the
  // catalog itself changes.
  const allUnits = React.useMemo(() => {
    const s = new Set();
    Object.values(formulationCatalog).forEach((e) => {
      if (!e || !Array.isArray(e.units)) return;
      e.units.forEach((u) => { if (u) s.add(u); });
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [formulationCatalog]);
  const otherUnits = allUnits.filter((u) => !availableUnits.includes(u));
  // Instructions chip set — driven entirely by the catalog. Empty
  // instructions[] (e.g. INHALER / DELIVERY SYSTEM) hides the
  // Instructions section. Keys map to INSTRUCTION_OPTIONS labels.
  // Ointment-family formulations declare `instructions: ["local"]`
  // here — the "Local application" chip is how the semantics land in
  // the UI; the Dose-quantity block uses the same Qty + unit controls
  // as every other formulation (0-unit case = free-text unit input).
  const availableInstructions = catalogInstructions
    .map((k) => INSTRUCTION_OPTIONS.find((o) => o.key === k))
    .filter(Boolean);
  // Auto-align unit fields with the formulation's declared units.
  //   1 Recommended  → auto-pick that unit for the scalar + every slot.
  //   N Recommended  → depends on WHY this effect fired:
  //     • the reviewer picked a different medicine (formulation
  //       changed) → always reset to "" so the picker starts fresh
  //       for the new medicine.
  //     • the catalog late-loaded for the SAME formulation → keep the
  //       current pick if it still belongs, else reset (avoids
  //       trashing a seeded pick on initial catalog load).
  //   0 Recommended  → clear picked unit; UnitPicker's placeholder
  //                    reads "None" until the reviewer picks from
  //                    Other for the rare corner case.
  const prevFormulationRef = useRef(formulation);
  useEffect(() => {
    // If the formulation catalog hasn't landed yet, `availableUnits` is
    // still []. Don't run the reset logic in that window — it would
    // blank the record's saved unit before the catalog arrives (Edit /
    // Update mode), and the later "preserve on load" branches (below)
    // would then keep that blank forever. Wait for a real catalog entry
    // before deciding.
    if (!catalogEntry) return;
    const formulationChanged = prevFormulationRef.current !== formulation;
    prevFormulationRef.current = formulation;
    if (availableUnits.length === 1) {
      // Fresh formulation: seed the single Recommended unit as the
      // default. On an unchanged formulation (Edit-mode load, or a
      // re-render after a state tweak), preserve the reviewer's saved
      // pick — including an explicit None ("") that got persisted.
      const only = availableUnits[0];
      if (formulationChanged) {
        setDoseUnit(only);
        setPerSlotQuantities((prev) => {
          const next = {};
          Object.keys(prev).forEach((slot) => {
            next[slot] = { value: prev[slot].value, unit: only };
          });
          return next;
        });
      } else {
        const keep = (prev) => (!prev || availableUnits.includes(prev) ? prev : only);
        setDoseUnit(keep);
        setPerSlotQuantities((prev) => {
          const next = {};
          Object.keys(prev).forEach((slot) => {
            next[slot] = { value: prev[slot].value, unit: keep(prev[slot].unit) };
          });
          return next;
        });
      }
    } else if (availableUnits.length > 1) {
      // Default to the first Recommended unit whenever a fresh
      // formulation lands — the reviewer can still change it or clear
      // it back to None from the picker. Within an unchanged formulation
      // we preserve the reviewer's own pick (including an explicit None,
      // which is a legitimate persisted state on Edit / Update loads);
      // only a TRUTHY-but-stale unit gets replaced with the default.
      const first = availableUnits[0];
      if (formulationChanged) {
        setDoseUnit(first);
        setPerSlotQuantities((prev) => {
          const next = {};
          Object.keys(prev).forEach((slot) => {
            next[slot] = { value: prev[slot].value, unit: first };
          });
          return next;
        });
      } else {
        const keep = (prev) => (!prev || availableUnits.includes(prev) ? prev : first);
        setDoseUnit(keep);
        setPerSlotQuantities((prev) => {
          const next = {};
          Object.keys(prev).forEach((slot) => {
            next[slot] = { value: prev[slot].value, unit: keep(prev[slot].unit) };
          });
          return next;
        });
      }
    } else {
      setDoseUnit("");
      setPerSlotQuantities((prev) => {
        const next = {};
        Object.keys(prev).forEach((slot) => {
          next[slot] = { value: prev[slot].value, unit: "" };
        });
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formulation, availableUnits.join("|")]);
  // Keep the instruction pick consistent with the formulation's
  // declared options. Instructions are otherwise optional (user may
  // pick or clear any chip) — the one exception is the ointment-family
  // case where the ONLY declared chip is "Local Application": nothing
  // else can go there, so we default-pick it on formulation change
  // when the field is empty. Non-empty state (Update mode, or a chip
  // the reviewer already picked) is preserved.
  useEffect(() => {
    if (availableInstructions.length === 0) {
      if (instructions) setInstructions("");
      return;
    }
    if (instructions && !availableInstructions.some((o) => o.key === instructions)) {
      setInstructions("");
      return;
    }
    if (
      !instructions &&
      availableInstructions.length === 1 &&
      availableInstructions[0].key === "local"
    ) {
      setInstructions("local");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formulation, availableInstructions.length, availableInstructions.map((o) => o.key).join("|")]);
  const timesOfDayHasAsNeeded = timesOfDay.includes(AS_NEEDED);
  // Canonical Morning → Afternoon → Evening → Night order for the dose
  // rows regardless of the reviewer's click sequence — sort by the
  // TIMES_OF_DAY declaration index so the rendered rows and the save
  // payload's iteration order stay consistent. `timesOfDay` state
  // itself still carries the click order (harmless — the picker chips
  // are always painted from TIMES_OF_DAY too).
  const namedSlots = timesOfDay
    .filter((t) => t !== AS_NEEDED)
    .sort((a, b) => TIMES_OF_DAY.indexOf(a) - TIMES_OF_DAY.indexOf(b));
  // "None" unit (empty string) opts the slot out of the dose check —
  // the Qty box is greyed / disabled in that state, so requiring a
  // positive number would leave a legitimate None-unit save unreachable.
  const scalarDoseOK = !doseUnit || isPositiveDose(doseValue);
  const perSlotDoseOK =
    namedSlots.length === 0 ||
    namedSlots.every((s) => {
      const q = perSlotQuantities[s] || {};
      return !q.unit || isPositiveDose(q.value);
    });
  const hasDose =
    frequencyMode === "timesOfDay"
      ? (timesOfDayHasAsNeeded ? scalarDoseOK : perSlotDoseOK)
      : scalarDoseOK;

  const isBusy = submitting || !!saving;
  // Free-text medicines are not allowed: Save is gated on a real
  // catalog binding (medicineCode.mCode). The reviewer / clinician
  // MUST pick a row from MedicineCatalogAutocomplete before the CTA
  // enables. In transcript mode, the autocomplete's own mount-time
  // prefetch + auto-pick-on-exact-match logic handles this
  // automatically when the AI's proposed name lines up with a
  // catalog row — the reviewer never has to click.
  const hasCatalogBinding = !!(medicineCode && medicineCode.mCode);
  // Guard against a manually-typed pre-1900 start date sneaking past the
  // native picker's min. Same treatment as other date fields in the CC/Dx
  // status forms.
  const startDateInvalid = !!medStartDate && medStartDate < "1900-01-01";
  const canSubmit =
    hasCatalogBinding &&
    hasDose &&
    !!medStartDate &&
    !startDateInvalid &&
    customFreqPairOK &&
    hasFrequency &&
    !isBusy &&
    !blockedBySibling &&
    (ongoing || effectiveDurationDays > 0);

  const close = () => onClose(formId);

  const submit = () => {
    if (!canSubmit) return;
    if (!isTranscript && !patientId) return;

    // Build the Med v2 body shape ONCE — transcript caller and standalone
    // svc.add path both use it. Transcript caller adapts snake_case at
    // its own callsite. Body shape reflects the redesigned dose model:
    //   perSlotQuantities  → Times-of-day mode with named slots picked
    //                         (Morning=2, Night=1). Only fields for
    //                         currently-selected slots are included so
    //                         the wire stays clean.
    //   doseQuantity       → scalar shape (Custom mode, Ointment, or
    //                         Times-of-day + As-needed which now carries
    //                         a real Qty + unit per the updated mock).
    //   timesOfDay         → may include the special "asNeeded" tag,
    //                         which is mutually exclusive with the four
    //                         named slots (see toggleTimeOfDay).
    // Backend contract will be updated to match; scope this pass is
    // frontend-only.
    const usePerSlot = frequencyMode === "timesOfDay" && namedSlots.length > 0;
    const perSlotClean = usePerSlot
      ? namedSlots.reduce((acc, s) => {
          const q = perSlotQuantities[s] || {};
          acc[s] = { value: String(q.value || "").trim(), unit: String(q.unit || "").trim() };
          return acc;
        }, {})
      : undefined;
    // Times-of-day + As-needed carries a scalar dose (Qty + unit row
    // alongside the As-needed slot). Custom mode always carries a
    // scalar. Ointment-family formulations use the same standard
    // Qty + unit controls.
    const scalarDose = usePerSlot
      ? undefined
      : { value: doseValue.trim(), unit: doseUnit.trim() };

    const body = {
      medicine: medicine.trim(),
      formulation: formulation.trim(),
      formulationName: formulationName.trim(),
      medicineCode: medicineCode || undefined,
      diagnosisId: diagnosisId || undefined,
      sinceWhen: toIsoUtc(medStartDate),
      medStartDate: toIsoUtc(medStartDate),
      medEndDate: derivedEndDate ? toIsoUtc(derivedEndDate) : undefined,
      ongoingIndefinitely: ongoing,
      durationDays: effectiveDurationDays,
      // Only ONE dose shape is populated per save — the OTHER is dropped
      // to keep the payload unambiguous for the write path + audit trail.
      doseQuantity: scalarDose,
      perSlotQuantities: perSlotClean,
      // Interval chips are UI shortcuts for the (value+unit) pair, so
      // there's no separate `interval` on the wire any more. Custom
      // mode sends the pair verbatim; Times-of-day sends only slots.
      doseFrequency: frequencyMode === "timesOfDay"
        ? { timesOfDay, customValue: undefined, customUnit: "" }
        : {
            timesOfDay: [],
            customValue: customFreqValue ? parseInt(customFreqValue, 10) || 0 : undefined,
            customUnit: customFreqUnit,
          },
      instructions,
      description: description.trim(),
    };

    // Transcript mode — hand out; caller owns persistence. Skip local
    // submitting/feedback + MED_V2_REFRESH emit.
    if (isTranscript) {
      onSave(body);
      return;
    }

    setSubmitting(true);
    setFeedback(null);
    if (typeof onSavingChange === "function") {
      onSavingChange(formId, true);
    }

    const svc = new MedicineService();
    const done = () => {
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      setSubmitting(false);
      EventService.emit(MED_V2_REFRESH);
      onClose(formId);
    };
    const failed = (err) => {
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      setSubmitting(false);
      setFeedback({
        kind: "err",
        text: (err && err.message) || "Save failed — please retry",
      });
    };
    // Fresh version per mutation, never one captured at mount — same
    // just-in-time read every other rail sheet does.
    svc.list(
      patientId,
      (doc) => {
        svc.add(
          patientId,
          { ...body, expectedVersion: (doc && doc.version) || 0 },
          done,
          failed
        );
      },
      failed
    );
  };

  const foldId = `med3-fold-${formId}`;
  const startId = `med3-start-${formId}`;
  const descId = `med3-desc-${formId}`;
  const rootClass = [
    "med3-dlg",
    collapsed ? "is-collapsed" : "",
    // Reuse the cc3-form theme utilities so the border-left + eyebrow
    // + Save button variants are shared across all v2 sections' forms
    // in transcript mode. med3-dlg keeps its own layout; the transcript
    // accent classes are additive.
    isTranscript ? "cc3-form" : "",
    themeClass || "",
  ].filter(Boolean).join(" ");
  const displayError = feedback && feedback.kind === "err"
    ? feedback.text
    : error || "";
  const displayErrorKind = displayError ? "err" : "";

  return (
    <section className={rootClass}>
      {/* ZY-10356 Phase 6 — top-right X close (design doc §3). The X
          appears only on re-opens of something committed earlier in
          this call (#2/#4/#6/#8) — first-opens leave Reject as the
          sole dismiss affordance. Fires the parent's onClose, which
          for a reedit card routes to revertCaptured (SAFE semantics)
          and for a revived rejected AI proposal routes to the local
          drop — Reject in the footer is still the destructive audit
          action. */}
      {isTranscript && isReopen ? (
        <button
          type="button"
          className="zy-card-x"
          onClick={() => onClose(formId)}
          aria-label="Close"
        >
          &times;
        </button>
      ) : null}
      {/* Board 2964:4275 — the Medicine label row is the collapse handle;
          the chevron folds everything below it, footer included. In
          transcript mode with lockName set, swap the "fuzzy search"
          hint for the read-only medicine name (matches ComplaintForm /
          DiagnosisForm's re-open convention). */}
      <div className="med3-lbl-row med3-handle">
        <span className="med3-lbl">
          {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
          Medicine
          {!lockMedicineName && <span className="cc3-req">*</span>}{" "}
          {lockMedicineName ? (
            <span className="cc3-update-name">&middot; {medicine || "(none)"}</span>
          ) : (
            <span className="med3-lbl-hint">fuzzy search</span>
          )}
        </span>
        <button
          type="button"
          className="med3-chev"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls={foldId}
          aria-label={collapsed ? "Expand this medicine" : "Collapse this medicine"}
        >
          <CollapseChevron />
        </button>
      </div>

      <div className="med3-fold" id={foldId}>
        <div className="med3-fold-inner">
          {!lockMedicineName && (
            <div className="med3-field">
              <MedicineCatalogAutocomplete
                ref={catalogRef}
                value={medicine}
                mCode={medicineCode && medicineCode.mCode}
                onChange={setMedicineFromCatalog}
                disabled={isBusy}
                isBlocked={isDropdownItemBlocked}
                onBlockedPick={onBlockedCatalogPick}
                isAlreadyActive={isDropdownItemAlreadyActive}
              />
            </div>
          )}

          {/* ── For · link to a diagnosed condition (2964:4287) ──
              Transcript mode hides the DiagnosisPicker per SECTIONS_SPEC.md
              simplification B: AI doesn't carry Dx ObjectIDs, and cross-
              service ID resolution mid-call is out of scope. Reviewer
              links via standalone editor after Save & next. */}
          {!isTranscript && (
            <div className="med3-field">
              <span className="med3-lbl">
                For <span className="med3-lbl-hint">&middot; link to a diagnosed condition</span>
              </span>
              <DiagnosisPicker
                patientId={patientId}
                value={diagnosisId}
                onChange={setDiagnosisId}
                disabled={isBusy}
              />
            </div>
          )}

          {/* ── Start date (2969:4255) ── */}
          <div className="med3-field">
            <label className="med3-lbl is-muted" htmlFor={startId}>
              Start date <span className="cc3-req">*</span>
            </label>
            {/* Start date accepts any date (past, today, or future) — a
                reviewer can log a medicine scheduled to begin later. No
                default; empty value makes the native picker open on
                today's month per browser default. */}
            <span className="med3-date">
              <input
                id={startId}
                className="med3-date-input"
                type="date"
                value={medStartDate}
                onChange={(e) => setMedStartDate(e.target.value)}
                min="1900-01-01"
                disabled={isBusy}
              />
              <span className="med3-date-icon" aria-hidden="true"><CalendarIcon /></span>
            </span>
            {startDateInvalid && (
              <p className="fam-field-error">Start date must be on or after 1 Jan 1900.</p>
            )}
            {derivedEndDate && (
              <p className="med3-autocalc">
                Auto-calc &middot; Med. End date <strong>{shortDate(derivedEndDate)}</strong>
              </p>
            )}
          </div>

          {/* ── Dose frequency ── mode toggle + mode-specific inner card.
              Times-of-day mode: four named slot chips + a separate
              "As needed" full-width row. Custom mode: interval chips
              (shortcuts that populate the customValue/customUnit=Hours
              pair) followed by an explicit value/unit pair.
              The toggle wipes the OTHER mode's state on switch so the
              save payload is always unambiguous. */}
          <div className="med3-field is-roomy">
            <span className="med3-lbl">
              Dose frequency <span className="cc3-req">*</span>
            </span>
            <div className="med3-mode" role="tablist" aria-label="Dose frequency mode">
              <button
                type="button"
                role="tab"
                aria-selected={frequencyMode === "timesOfDay"}
                className={`med3-mode-btn${frequencyMode === "timesOfDay" ? " is-on" : ""}`}
                onClick={() => switchFrequencyMode("timesOfDay")}
                disabled={isBusy}
              >
                Times of day
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={frequencyMode === "custom"}
                className={`med3-mode-btn${frequencyMode === "custom" ? " is-on" : ""}`}
                onClick={() => switchFrequencyMode("custom")}
                disabled={isBusy}
              >
                Custom
              </button>
            </div>

            {/* Single bordered card that holds BOTH the frequency
                picker AND the dose-quantity subsection. The mode
                toggle stays OUTSIDE the border so the picker + its
                dose belong to the same visual grouping regardless of
                which mode is active. */}
            <div className="med3-freq-card">
              {frequencyMode === "timesOfDay" ? (
                <>
                  <span className="med3-freq-card-lbl">
                    Times of day <span className="cc3-req">*</span>{" "}
                    <span className="med3-lbl-hint">&middot; multi-select</span>
                  </span>
                  <div className="med3-slot-row" role="group">
                    {TIMES_OF_DAY.map((t) => (
                      <button
                        key={t}
                        type="button"
                        className={`med3-slot-btn${timesOfDay.includes(t) ? " is-on" : ""}`}
                        aria-pressed={timesOfDay.includes(t)}
                        onClick={() => toggleTimeOfDay(t)}
                        disabled={isBusy}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <p className="med3-freq-or"><span>or</span></p>
                  <button
                    type="button"
                    className={`med3-asneeded-btn${timesOfDayHasAsNeeded ? " is-on" : ""}`}
                    aria-pressed={timesOfDayHasAsNeeded}
                    onClick={() => toggleTimeOfDay(AS_NEEDED)}
                    disabled={isBusy}
                  >
                    As needed
                  </button>
                </>
              ) : (
                <>
                  <span className="med3-freq-card-lbl">
                    Interval <span className="cc3-req">*</span>{" "}
                    <span className="med3-lbl-hint">&middot; single-select</span>
                  </span>
                  <div className="med3-slot-row" role="group">
                    {INTERVAL_OPTIONS.map((iv) => {
                      const on = intervalChipMatches(iv);
                      return (
                        <button
                          key={iv}
                          type="button"
                          className={`med3-slot-btn${on ? " is-on" : ""}`}
                          aria-pressed={on}
                          onClick={() => pickInterval(iv)}
                          disabled={isBusy}
                        >
                          {iv}
                        </button>
                      );
                    })}
                  </div>
                  <div className="med3-two">
                    <div className="med3-field">
                      <input
                        className="med3-input"
                        type="number"
                        min={1}
                        max={customFreqMax(customFreqUnit)}
                        value={customFreqValue}
                        onChange={(e) => changeCustomFreqValue(e.target.value)}
                        placeholder="—"
                        aria-label="Custom frequency value"
                        disabled={isBusy}
                      />
                    </div>
                    <div className="med3-field">
                      <Select
                        value={customFreqUnit}
                        options={[
                          ...FREQ_UNIT_OPTIONS.map((u) => ({ key: u, label: u })),
                        ]}
                        getKey={(o) => o.key}
                        getLabel={(o) => o.label}
                        onChange={changeCustomFreqUnit}
                        menuClassName={menuClassName || ""}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* ── Dose quantity subsection ── nested inside the same
                  bordered card as the frequency picker. Shape depends
                  on formulation + mode:
                    Ointment-like → single "Local application" text.
                    Times-of-day + As needed → hidden with a hint.
                    Times-of-day + named slots → one row per slot.
                    Custom mode → preset ladder + scalar + As needed
                                  checkbox. */}
              <div className="med3-dose-head med3-dose-head-nested">
                <span className="med3-freq-card-lbl">
                  Dose quantity<span className="cc3-req">*</span>
                </span>
                <span className="med3-formulation-pill" title="Formulation from the catalog">
                  <span className="med3-formulation-pill-lbl">FORMULATION</span>
                  <span className="med3-formulation-pill-val">
                    {formulationName
                      || (catalogEntry && catalogEntry.label)
                      || formulation
                      || "—"}
                  </span>
                </span>
              </div>

              {frequencyMode === "timesOfDay" ? (
                timesOfDayHasAsNeeded ? (
                  // As-needed slot picked: single scalar row (Qty +
                  // unit) — no preset ladder, no per-slot rows. Same
                  // 0/1/N unit renderer as Custom mode.
                  <div className="med3-dose-row">
                    {/* Qty is disabled while no unit is picked (including
                        an explicit "None") — a quantity without a unit is
                        never a meaningful dose. */}
                    <input
                      className="med3-input"
                      type="text"
                      value={doseValue}
                      onChange={(e) => setDoseValue(clampDoseValue(e.target.value))}
                      placeholder="Qty"
                      aria-label="Dose quantity"
                      disabled={isBusy || !doseUnit}
                    />
                    <UnitPicker
                      value={doseUnit}
                      recommended={availableUnits}
                      other={otherUnits}
                      onChange={(u) => {
                        setDoseUnit(u);
                        // Picking None clears the Qty box — a stale
                        // number sitting in a greyed-out input reads as
                        // a live value on save/reload otherwise.
                        if (!u) setDoseValue("");
                      }}
                      disabled={isBusy}
                      ariaLabel="Dose unit"
                    />
                  </div>
                ) : namedSlots.length === 0 ? (
                  <p className="med3-dose-hint">Select times of day to set quantities</p>
                ) : (
                  <div className="med3-slot-doses">
                    {namedSlots.map((slot) => {
                      const q = perSlotQuantities[slot] || { value: "", unit: "" };
                      return (
                        <div className="med3-slot-dose-row" key={slot}>
                          <span className="med3-slot-dose-lbl">{slot}</span>
                          <input
                            className="med3-input"
                            type="text"
                            value={q.value}
                            onChange={(e) => setSlotValue(slot, clampDoseValue(e.target.value))}
                            placeholder="Qty"
                            aria-label={`${slot} dose quantity`}
                            disabled={isBusy || !q.unit}
                          />
                          <UnitPicker
                            value={q.unit}
                            recommended={availableUnits}
                            other={otherUnits}
                            onChange={(u) => setSlotUnit(slot, u)}
                            disabled={isBusy}
                            ariaLabel={`${slot} dose unit`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                <>
                  <SegStrip
                    rows={[DOSE_QUICKPICK.map((v) => ({ key: v, label: v }))]}
                    isOn={(k) => doseValue === k}
                    onToggle={(k) => setDoseValue(doseValue === k ? "" : k)}
                    disabled={isBusy || !doseUnit}
                    boldOn
                  />
                  <div className="med3-dose-row">
                    <input
                      className="med3-input"
                      type="text"
                      value={doseValue}
                      onChange={(e) => setDoseValue(clampDoseValue(e.target.value))}
                      placeholder="Qty"
                      aria-label="Dose quantity"
                      disabled={isBusy || !doseUnit}
                    />
                    <UnitPicker
                      value={doseUnit}
                      recommended={availableUnits}
                      other={otherUnits}
                      onChange={(u) => {
                        setDoseUnit(u);
                        // Picking None clears the Qty box — a stale
                        // number sitting in a greyed-out input reads as
                        // a live value on save/reload otherwise.
                        if (!u) setDoseValue("");
                      }}
                      disabled={isBusy}
                      ariaLabel="Dose unit"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Instructions ── driven by per-formulation options:
              standard three (before / after / with meal), single
              "Local application" chip for ointment-family formulations
              (renders full-width thanks to `.med3-chip { flex: 1 1 0 }`),
              or hidden entirely when the formulation declares none.
              Instructions are optional — clicking the currently-picked
              chip clears the selection. ── */}
          {availableInstructions.length > 0 && (
            <div className="med3-field">
              <span className="med3-lbl">Instructions</span>
              <div className="med3-chips" role="group" aria-label="Instructions">
                {availableInstructions.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    className={`med3-chip${instructions === o.key ? " is-on" : ""}`}
                    aria-pressed={instructions === o.key}
                    onClick={() =>
                      setInstructions(instructions === o.key ? "" : o.key)
                    }
                    disabled={isBusy}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Duration (2964:4360) ── */}
          <div className="med3-field is-roomy">
            <div className="med3-duration-head">
              <span className="med3-lbl">
                Duration <span className="cc3-req">*</span>{" "}
                <span className="med3-lbl-hint">&middot; days / weeks</span>
              </span>
              <label className="med3-toggle-wrap">
                <input
                  type="checkbox"
                  checked={ongoing}
                  onChange={(e) => setOngoing(e.target.checked)}
                  disabled={isBusy}
                />
                <span className="med3-toggle" aria-hidden="true" />
                <span className="med3-toggle-lbl">Ongoing</span>
              </label>
            </div>
            {/* Board 2964:4370 — Ongoing DIMS the whole stack to 45% rather
                than unmounting it, so the layout never jumps. The controls
                are genuinely inert while dimmed. */}
            <div className={`med3-duration-stack${ongoing ? " is-dimmed" : ""}`}>
              <SegStrip
                rows={[
                  DURATION_DAYS_QUICKPICK.map((d) => ({ key: String(d), label: String(d) })),
                  DURATION_LONG_QUICKPICK.map((d) => ({ key: d.label, label: d.label })),
                ]}
                isOn={(k) => {
                  if (customDays || ongoing) return false;
                  const long = DURATION_LONG_QUICKPICK.find((d) => d.label === k);
                  if (long) {
                    // A day-count reachable from BOTH rows (7 → "1 week")
                    // lights only the cell that was actually clicked.
                    return durationDays === long.days && lastPickRef.current === k;
                  }
                  return durationDays === Number(k) && lastPickRef.current === k;
                }}
                onToggle={(k) => {
                  if (ongoing) return;
                  lastPickRef.current = k;
                  const long = DURATION_LONG_QUICKPICK.find((d) => d.label === k);
                  pickDays(long ? long.days : Number(k));
                }}
                disabled={ongoing || isBusy}
                footer={
                  // Custom-days row, painted inside the same bordered
                  // card as the chip rows. Left half is the numeric
                  // input, right half is a static "Days" label so the
                  // unit reads naturally alongside the number.
                  <div className="med3-seg-row med3-seg-custom-row">
                    {/* Custom days clamps to [1, 999] — the native `min` /
                        `max` attrs cap the spinner buttons AND the browser's
                        default validation, but Chrome / Safari still accept
                        keyboard-typed values past them; the onChange handler
                        strips non-digits and clamps so the state is always
                        a stringified integer in-range (or empty). */}
                    <input
                      className="med3-seg-custom-input"
                      type="number"
                      min="1"
                      max="999"
                      value={customDays}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") { setCustomDays(""); return; }
                        const n = parseInt(raw.replace(/[^0-9]/g, ""), 10);
                        if (isNaN(n)) { setCustomDays(""); return; }
                        setCustomDays(String(Math.min(999, Math.max(1, n))));
                      }}
                      placeholder="Custom - e.g. 21"
                      aria-label="Custom days"
                      disabled={ongoing || isBusy}
                    />
                    <span className="med3-seg-custom-unit">Days</span>
                  </div>
                }
              />
            </div>
          </div>

          {/* ── Description (2964:4400) ── */}
          <div className="med3-field">
            <label className="med3-lbl med3-desc-lbl" htmlFor={descId}>
              Description <span className="med3-lbl-hint">&middot; optional</span>
              <span className="med3-words">
                {description
                  ? `${countWords(description)} / ${MAX_DESCRIPTION_WORDS} words`
                  : `Max ${MAX_DESCRIPTION_WORDS} words`}
              </span>
            </label>
            <textarea
              id={descId}
              className="med3-textarea"
              rows={3}
              value={description}
              onChange={(e) =>
                setDescription(clampWords(e.target.value, MAX_DESCRIPTION_WORDS))
              }
              placeholder="Provide description here"
              disabled={isBusy}
            />
          </div>

          {displayError && (
            <div className={`cc-feedback is-${displayErrorKind}`}>{displayError}</div>
          )}

          {/* ── Footer (2964:4407) — equal halves ── */}
          <footer className="med3-foot">
            {/* Never disabled while saving: a write that never settles would
                otherwise pin the sheet open with no way out. */}
            <button
              type="button"
              className="med3-btn med3-btn-sec"
              onClick={() => {
                if (isTranscript && !isBusy && typeof onReject === "function") {
                  onReject();
                  return;
                }
                close();
              }}
            >
              {isBusy ? "Close" : (isTranscript ? "Reject" : "Cancel")}
            </button>
            <button
              type="button"
              className={`med3-btn med3-btn-pri${themeClass ? ` cc3-save ${themeClass}` : ""}`}
              onClick={submit}
              disabled={!canSubmit}
              title={blockedBySibling ? "Another medicine is saving" : undefined}
            >
              {isBusy
                ? (isTranscript ? "Adding…" : "Saving…")
                : isTranscript
                ? "Add"
                : "Save medication"}
            </button>
          </footer>
        </div>
      </div>

      {allergyDialogOpen && blockingMatch && (
        <AllergyGuardDialog
          match={blockingMatch}
          onOkay={dismissAllergyDialog}
          onViewAllergies={openAllergiesSection}
        />
      )}
    </section>
  );
};

MedicineForm.propTypes = {
  patientId: PropTypes.number,
  formId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  ordinal: PropTypes.number,
  showOrdinal: PropTypes.bool,
  blockedBySibling: PropTypes.bool,
  onFilledChange: PropTypes.func,
  onSavingChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  // Transcript-mode extensions — all optional; unset preserves standalone.
  /**
   * AI-captured payload used to pre-fill the form (add mode only). Same
   * shape the agent emits for medication captures: {medicine, formulation,
   * dose_qty, frequency, time_period, start_date, end_date, instructions,
   * status, description}. Best-effort parses of `dose_qty` /
   * `frequency` / `time_period` mirror the Go adapter so the frontend
   * card + Save & next fan-out land the same values.
   */
  initialPayload: PropTypes.object,
  /**
   * When provided, the form skips its own Med v2 write path and hands the
   * Med v2 request body to this callback. Caller owns persistence — used
   * by the transcript flow to stash captured items on the transcript
   * instead of writing directly to Med v2. Automatically hides the
   * DiagnosisPicker (linked-Dx picker) per SECTIONS_SPEC.md
   * simplification B.
   */
  onSave: PropTypes.func,
  onReject: PropTypes.func,
  saving: PropTypes.bool,
  error: PropTypes.string,
  themeClass: PropTypes.string,
  /**
   * Lock the medicine name (catalog autocomplete) as read-only. Add-mode
   * transcript callers set true when reopening an already-saved captured
   * item (review click-to-edit) so the reviewer can only tweak dose /
   * frequency / duration / description from there.
   */
  lockName: PropTypes.bool,
  /**
   * Transcript-mode dedup set — mCodes already represented in the
   * left-side ACTIVE table (currently-active Med v2 rows + in-call
   * rejects + in-call adds). Past (completed / discontinued) rows
   * are deliberately excluded so a drug the patient finished earlier
   * can be re-prescribed. Picks that match any entry here flag
   * "Already added" in the catalog dropdown. Unset in standalone —
   * the local useEffect fetches active mCodes direct from Med v2.
   */
  blockedMcodes: PropTypes.instanceOf(Set),
  /**
   * Extra class routed onto portalled Select menus (frequency unit
   * dropdown). Transcript callers pass "ait-menu" so the menu
   * stacks above the transcript modal overlay — standalone defaults
   * to "" and the base z-index applies.
   */
  menuClassName: PropTypes.string,
  /**
   * ZY-10356 Phase 6 — card is a re-open of something committed
   * earlier in this call (reedit, or AI proposal that revived a
   * previously-rejected row). Gates the top-right X close button;
   * first-opens leave the footer Reject as the only dismiss. Fires
   * `onClose` (SAFE revert semantics) rather than `onReject`.
   */
  isReopen: PropTypes.bool,
};

MedicineForm.defaultProps = {
  initialPayload: null,
  onSave: null,
  saving: false,
  error: "",
  themeClass: "",
  lockName: false,
  blockedMcodes: null,
  menuClassName: "",
  isReopen: false,
};

// ── Allergy-guard building blocks ────────────────────────────────
// Kept as small helpers in this file (rather than a separate module)
// because they're only meaningful in the Add-medicine context — the
// Update sheet doesn't need them since the medicine identity is locked
// at that point.

const reactionsText = (allergy) => {
  const rs = (allergy && allergy.reactions) || [];
  const names = rs.map((r) => r && r.name).filter(Boolean);
  return names.length > 0 ? names.join(", ") : "";
};

const noticeDateText = (allergy) => {
  const iso = (allergy && (allergy.firstNoted || allergy.lastReaction)) || null;
  if (!iso) return "";
  return formatShortDate(iso);
};

// Blocking overlay dialog — the "cannot prescribe" message from the mock,
// worded to explain WHY the match fired (same catalog entry, shared
// salt, or overlapping name). Renders inside the rail sheet as an
// absolutely-positioned card so it doesn't need to escape the
// .med3-editor column.
const AllergyGuardDialog = ({ match, onOkay, onViewAllergies }) => {
  const { allergy } = match;
  const reactions = reactionsText(allergy);
  const noticeDate = noticeDateText(allergy);

  // Allergies are salt-level now — the allergy's `.name` IS the salt.
  // Saying the picked medicine "contains X" is more direct than the
  // earlier "matches via a shared salt (X)" phrasing (which restated
  // what the header already showed).
  const matchExplanation = (
    <>
      The selected medicine contains <strong>{allergy.name}</strong> and
      may trigger the same reaction.
    </>
  );

  return (
    <div className="med3-allergy-modal-backdrop" onMouseDown={onOkay}>
      <div
        className="med3-allergy-modal"
        role="alertdialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="med3-allergy-modal-alert">
          <p className="med3-allergy-modal-title">
            <span aria-hidden="true">⚠</span> Salt allergy — cannot prescribe
          </p>
          <p className="med3-allergy-modal-body">
            A salt allergy to <strong>{allergy.name}</strong> is on record
            for this patient
            {(reactions || noticeDate) && (
              <>
                {" "}(
                {reactions && <>reactions: {reactions}</>}
                {reactions && noticeDate && <> — </>}
                {noticeDate && <>noted {noticeDate}</>}
                )
              </>
            )}
            . {matchExplanation}
          </p>
          <p className="med3-allergy-modal-block">
            Prescribing is blocked for this medicine.
          </p>
        </div>
        <div className="med3-allergy-modal-foot">
          <button
            type="button"
            className="med3-btn med3-btn-sec"
            onClick={onViewAllergies}
          >
            View allergy records
          </button>
          <button
            type="button"
            className="med3-btn med3-btn-pri"
            onClick={onOkay}
            autoFocus
          >
            Okay
          </button>
        </div>
      </div>
    </div>
  );
};
AllergyGuardDialog.propTypes = {
  match: PropTypes.shape({
    allergy: PropTypes.object.isRequired,
    matchType: PropTypes.oneOf(["mcode", "salt", "name"]).isRequired,
    matchDetail: PropTypes.string,
  }).isRequired,
  onOkay: PropTypes.func.isRequired,
  onViewAllergies: PropTypes.func.isRequired,
};

export default MedicineForm;
